import { useState } from 'react';

import { ChakraFlex, GooglePlacesAutoCompleteV2 } from '@equip.health/ui';

import { VITE_GOOGLE_PLACES_API_KEY } from '~/lib/constants/env';
import { EDITBASICINFO_ADDRESS_REQUIRED_FIELDS } from '~/lib/constants/patients.constants';
import useTimezone from '~/lib/hooks/useTimezone';
import {
  getHighlightFields,
  getIsShippingAddressFieldEmpty,
  isShippingSameAsMainAddressV2,
  patientEnumMenuOptionsMapper,
  patientStatesEnumMenuOptionsMapper,
  toPatientAddress,
} from '~/lib/util/patient.util';
import {
  useGetProviderStatesEnumQuery,
  useGetProviderTimezonesEnumQuery,
} from '~/store/services/enumerations';

interface PatientProfileAddressPrps {
  appendSurveyResult: Partial<SurveyResults>;
  mainAddress: GooglePlacesField;
  shippingAddress: GooglePlacesField;
  updateSection: (
    section: 'mainAddress' | 'shippingAddress',
    updatedData: GooglePlacesField,
  ) => void;
  updateField: (key: string, value: boolean) => void;
}

const PatientProfileAddress = ({
  appendSurveyResult,
  mainAddress,
  updateSection,
  shippingAddress,
  updateField,
}: PatientProfileAddressPrps) => {
  const TOP_LEVEL_ID = 'patient-profile-address';
  const { data: states, isLoading: isGetStatesLoading } =
    useGetProviderStatesEnumQuery();
  const isShippingAddressFieldEmpty =
    getIsShippingAddressFieldEmpty(shippingAddress);

  const statesMenuOptions = patientStatesEnumMenuOptionsMapper(states);
  const { data: timezones, isLoading: isGetTimezonesLoading } =
    useGetProviderTimezonesEnumQuery();
  const timezonesMenuOptions = patientEnumMenuOptionsMapper(timezones);
  const { getTimezoneFromLatLong, timezoneData } = useTimezone();
  const [sameAsMainAddress, setSameAsMainAddress] = useState<boolean>(
    isShippingSameAsMainAddressV2(
      toPatientAddress(mainAddress),
      toPatientAddress(shippingAddress),
    ),
  );

  const handleAddressChange = (
    section: 'mainAddress' | 'shippingAddress',
    updatedAddressField: GooglePlacesField,
  ): void => {
    const isShipping = section === 'shippingAddress';
    const mainAddr = toPatientAddress(mainAddress);
    const shipAddr = toPatientAddress(shippingAddress);
    const updatedAddr = toPatientAddress(updatedAddressField);

    setSameAsMainAddress(
      isShipping
        ? isShippingSameAsMainAddressV2(updatedAddr, mainAddr)
        : isShippingSameAsMainAddressV2(shipAddr, updatedAddr),
    );

    updateSection(section, updatedAddressField);
  };
  return (
    <ChakraFlex flexDirection="column" gap="18px" minHeight="600px">
      <GooglePlacesAutoCompleteV2
        addressData={mainAddress}
        apiKey={VITE_GOOGLE_PLACES_API_KEY}
        getTimezone={getTimezoneFromLatLong}
        highlightFields={getHighlightFields(appendSurveyResult)}
        id={`${TOP_LEVEL_ID}__main-address`}
        isGetStatesLoading={isGetStatesLoading}
        isGetTimezonesLoading={isGetTimezonesLoading}
        label="Main address"
        requiredFields={EDITBASICINFO_ADDRESS_REQUIRED_FIELDS}
        saveAddressData={(data: GooglePlacesField) =>
          handleAddressChange('mainAddress', data)
        }
        shouldShowAsteriskForLabel
        shouldShowLabel
        shouldShowOrganizationName={false}
        shouldShowTimezone
        showAddressFields
        showUseMainAddressCheckbox={false}
        stateOptions={statesMenuOptions}
        timezoneData={timezoneData}
        timezoneOptions={timezonesMenuOptions}
        updateAddress={(
          value: string | { value: string; label: string },
          field: string,
        ) => {
          const fieldValue = typeof value === 'object' ? value?.value : value;
          const updatedData = { ...mainAddress, [field]: fieldValue };
          handleAddressChange('mainAddress', updatedData);
        }}
      />
      <GooglePlacesAutoCompleteV2
        addressData={shippingAddress}
        apiKey={VITE_GOOGLE_PLACES_API_KEY}
        id={`${TOP_LEVEL_ID}__shipping-address`}
        isGetStatesLoading={isGetStatesLoading}
        isGetTimezonesLoading={isGetTimezonesLoading}
        isSameAsChecked={sameAsMainAddress}
        label="Shipping address"
        onSameAsCheckboxClick={(value) => {
          setSameAsMainAddress(value);
          updateField('hasUnsavedChanges', true);
          if (value) {
            handleAddressChange('shippingAddress', mainAddress);
          }
        }}
        requiredFields={
          isShippingAddressFieldEmpty
            ? []
            : EDITBASICINFO_ADDRESS_REQUIRED_FIELDS
        }
        saveAddressData={(data: GooglePlacesField) =>
          handleAddressChange('shippingAddress', data)
        }
        shouldShowAsteriskForLabel={!isShippingAddressFieldEmpty}
        shouldShowLabel
        shouldShowOrganizationName={false}
        shouldShowTimezone={false}
        showAddressFields
        showUseMainAddressCheckbox
        stateOptions={statesMenuOptions}
        timezoneOptions={timezonesMenuOptions}
        updateAddress={(
          value: string | { value: string; label: string },
          field: string,
        ) => {
          const fieldValue = typeof value === 'object' ? value?.value : value;
          const updatedData = {
            ...shippingAddress,
            [field]: fieldValue,
          };
          handleAddressChange('shippingAddress', updatedData);
        }}
      />
    </ChakraFlex>
  );
};
export default PatientProfileAddress;
